import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Badge } from "@shopify/polaris";
import { toast } from "sonner";
import { AppDispatch } from "configureStore";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
  setModalxStyle,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchOnboardingWorkSecurityStatus,
  resendWorkSecuritySignature,
} from "services/onboarding.service";
import { fetchWorkSecurityAssignments } from "services/work-security.service";
import { parsePositionIds } from "services/positions.service";
import ApprovalStatusLine from "features/work-security-approvals/ApprovalStatusLine";
import { useOnboardingApprovals } from "features/work-security-approvals/useOnboardingApprovals";
import { OnboardingWorkSecurityStatus } from "types/Onboarding";
import { isEmptyUploadError } from "services/upload-file.service";
import {
  fetchAmendments,
  createAmendment,
  updateAmendment,
  deleteAmendment,
  generateAmendment,
  sendAmendmentForSignature,
  uploadSignedAmendment,
  revokeAmendment,
  completeAmendmentWsRevision,
  uploadAmendmentFile,
} from "services/contract-amendment.service";
import type { AmendmentChange } from "services/contract-amendment.service";
import { fetchTabUsers } from "services/signontab.service";
import { getSignatureUserLabel, STEP_LABELS } from "utils/onboardingStatus";
import DatePickerx from "features/datepicker/date-picker";
import ConfirmDialog from "components/confirm-dialog/confirm-dialog";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import { useConfirmDialog } from "components/confirm-dialog/use-confirm-dialog";
import { API_URL } from "../../constants";
import AmendmentChangesEditor from "./AmendmentChangesEditor";
import {
  AMENDMENT_NUMBER_MAX_LENGTH,
  describeAmendmentError,
  hasKnownAmendmentCode,
} from "./amendment-errors";
import EmploymentTermsFields from "./EmploymentTermsFields";
import RegisterAmendmentForm from "./RegisterAmendmentForm";
import SetAmendmentTermsForm from "./SetAmendmentTermsForm";
import TermGroupChips from "./TermGroupChips";
import {
  buildEmploymentTermsPayload,
  changeRowsFromAmendment,
  changesAnyTermGroup,
  draftFromAmendment,
  EMPTY_CHANGE_ROW,
  EMPTY_TERMS_DRAFT,
  hasAnyTerms,
  termsErrorKey,
  withoutEmptyChanges,
} from "./employment-terms";
import type { CurrentTerms } from "./employment-terms";

type AmendmentSignMethod = "dokobit" | "signontab" | "manual";
type SignOnTabTablet = { tabId: string; name: string };

export interface ContractAmendmentsPanelProps {
  companyId: string;
  onboardingId: string;
  readOnly?: boolean;
  /** True only when an active employment-leave process locks the hire. */
  leaveLocked?: boolean;
  canManageOnboarding?: boolean;
  lang?: "lt" | "en";
  onSendLoginLink?: () => void | Promise<void>;
  employeeUserId?: string;
  employeeName?: string;
  /**
   * Reload the onboarding record itself — after a manually signed amendment
   * is uploaded and after the legacy «tvarkų peržiūra» completes (both can
   * change the record's terms).
   */
  onReloadOnboarding?: () => void;
  /** After the amendments list changed (create / edit / delete / generate / send / revoke). */
  onChanged?: () => void;
  currentTerms?: CurrentTerms;
}

/**
 * Contract amendments (sutarties pakeitimai) of one onboarding record:
 * list + create / edit draft (incl. employment terms and the pareigybės
 * picker) / generate / send for signing (Dokobit, SignOnTab, manual upload)
 * / revoke, registering an old amendment that was signed on paper (scan),
 * stating the terms of a signed free-text amendment, plus the per-amendment
 * approvals line and the legacy «tvarkų peržiūra» block. Rendered by the onboarding record view and the
 * «darbuotojo kortelė» («Sutartis ir pakeitimai»), both to onboarding
 * managers only (Master / 'Access onboarding'); `canManageOnboarding` is
 * false there only while a job leave locks the hire.
 */
export default function ContractAmendmentsPanel({
  companyId,
  onboardingId,
  readOnly = false,
  leaveLocked = false,
  canManageOnboarding = false,
  lang = "en",
  onSendLoginLink,
  employeeUserId,
  employeeName = "",
  onReloadOnboarding,
  currentTerms,
  onChanged,
}: ContractAmendmentsPanelProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [amendments, setAmendments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // Opened from other buttons than the create / edit form; only one form is open at a time.
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [settingTermsForId, setSettingTermsForId] = useState<string | null>(null);
  const [editingRegisteredId, setEditingRegisteredId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { confirmState, askConfirm, handleConfirmYes, closeConfirm } = useConfirmDialog();

  const [wsStatus, setWsStatus] = useState<OnboardingWorkSecurityStatus | null>(null);
  const [wsLoading, setWsLoading] = useState(false);
  const [wsLoadFailed, setWsLoadFailed] = useState(false);
  const [wsAckDocless, setWsAckDocless] = useState(false);
  const [wsResendingFileId, setWsResendingFileId] = useState<string | null>(null);
  const [wsResentFileIds, setWsResentFileIds] = useState<Set<string>>(new Set());

  // Form state
  const [amendmentNumber, setAmendmentNumber] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [changes, setChanges] = useState<AmendmentChange[]>([EMPTY_CHANGE_ROW]);
  const [termsDraft, setTermsDraft] = useState(EMPTY_TERMS_DRAFT);
  const [signMethodsByAmendment, setSignMethodsByAmendment] = useState<
    Record<string, AmendmentSignMethod>
  >({});
  // SignOnTab tablets: loaded per company the first time someone picks SignOnTab.
  const [tabUsers, setTabUsers] = useState<{ companyId: string; list: SignOnTabTablet[] } | null>(null);
  const [tabUsersFailedFor, setTabUsersFailedFor] = useState<string | null>(null);
  const [selectedTabUser, setSelectedTabUser] = useState("");
  const wantsSignOnTab = Object.values(signMethodsByAmendment).includes("signontab");
  const companyTabUsers = tabUsers?.companyId === companyId ? tabUsers.list : null;
  const tabUsersFailed = tabUsersFailedFor === companyId;
  const tabUserReady = !!companyTabUsers?.some((u) => u.tabId === selectedTabUser);

  useEffect(() => {
    if (!wantsSignOnTab || companyTabUsers !== null || tabUsersFailed) return;
    let cancelled = false;
    fetchTabUsers(companyId)
      .then((data: SignOnTabTablet[]) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setTabUsers({ companyId, list });
        setSelectedTabUser((prev) =>
          list.some((u) => u.tabId === prev) ? prev : list[0]?.tabId || ""
        );
      })
      .catch(() => {
        // Not "no tablets": keep the list unloaded and offer a retry.
        if (!cancelled) setTabUsersFailedFor(companyId);
      });
    return () => {
      cancelled = true;
    };
  }, [wantsSignOnTab, companyTabUsers, tabUsersFailed, companyId]);

  useEffect(() => {
    loadData();
  }, [companyId, onboardingId]);

  const needsWsRevision = amendments.some(
    (a) =>
      a.status === "signed" &&
      (a.newPositionLT || a.newPositionEN) &&
      a.wsReviewStatus === "pending"
  );

  // New-style position amendments (no legacy «tvarkų peržiūra»): the
  // pareigybės follow per-sphere approvals — show their state per amendment.
  const isApprovalAmendment = (a: any) =>
    a.status === "signed" &&
    a.wsReviewStatus !== "pending" &&
    (parsePositionIds(a.newPositionIds).length > 0 || a.newPositionLT || a.newPositionEN);
  const amendmentApprovals = useOnboardingApprovals(
    companyId,
    onboardingId,
    amendments.some(isApprovalAmendment)
  );
  const approvalsForAmendment = (amendmentId: string) =>
    amendmentApprovals.rows.filter((row) => row.amendmentId === amendmentId);
  // A newly signed amendment brings new approval rows — refetch when the set
  // of such amendments changes, not only when the first one appears.
  const approvalAmendmentKey = amendments
    .filter(isApprovalAmendment)
    .map((a) => a.id)
    .join(",");
  const reloadAmendmentApprovals = amendmentApprovals.reload;
  useEffect(() => {
    if (approvalAmendmentKey) reloadAmendmentApprovals();
  }, [approvalAmendmentKey, reloadAmendmentApprovals]);

  const loadWsStatus = async () => {
    setWsLoading(true);
    setWsLoadFailed(false);
    try {
      const data = await fetchOnboardingWorkSecurityStatus(companyId, onboardingId);
      setWsStatus(data);
    } catch {
      setWsStatus(null);
      setWsLoadFailed(true);
    } finally {
      setWsLoading(false);
    }
  };

  useEffect(() => {
    if (needsWsRevision && canManageOnboarding) {
      loadWsStatus();
    }
  }, [needsWsRevision, canManageOnboarding, companyId, onboardingId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchAmendments(companyId, onboardingId);
      setAmendments(data || []);
    } catch {
      setAmendments([]);
    } finally {
      setLoading(false);
    }
  };

  // A registered amendment or newly set terms change what «Sutarties
  // informacija» shows, so the record is reloaded along with the list.
  const reloadAfterTermsChange = async () => {
    await loadData();
    onReloadOnboarding?.();
    // The personnel card passes one function for both — fetch its summary once.
    if (onChanged !== onReloadOnboarding) onChanged?.();
  };

  const refreshAll = async () => {
    await loadData();
    onReloadOnboarding?.();
    await loadWsStatus();
  };

  const handleAmendmentWsResend = async (fileId: string) => {
    setWsResendingFileId(fileId);
    try {
      await resendWorkSecuritySignature(companyId, onboardingId, fileId);
      setWsResentFileIds((prev) => new Set(prev).add(fileId));
      await loadWsStatus();
    } catch {
      toast.error(t("error"));
    } finally {
      setWsResendingFileId(null);
    }
  };

  const handleAmendmentManageWsRoles = async () => {
    if (!employeeUserId) {
      toast.error(t("error"));
      return;
    }
    try {
      const data = await fetchWorkSecurityAssignments(companyId, {});
      dispatch(setModalxOptions({ title: t("assign_user_roles") }));
      dispatch(setModalxComponent("WorkSecurityUserRolesModal"));
      dispatch(
        setModalxComponentProps({
          userId: employeeUserId,
          userName: employeeName,
          availableRoles: data.roles || [],
          onSuccess: () => loadWsStatus(),
        })
      );
      dispatch(setIsModalxOpen(true));
    } catch {
      toast.error(t("error"));
    }
  };

  const handleCompleteWsRevision = (amendmentId: string) => {
    if (!wsStatus) return;
    const doComplete = async (confirmNoRoles: boolean) => {
      setActionLoading(true);
      try {
        await completeAmendmentWsRevision(companyId, amendmentId, {
          ...(confirmNoRoles ? { confirmNoRoles: true } : {}),
          ...(wsAckDocless ? { acknowledgeDoclessRoles: true } : {}),
        });
        toast.success(t("amendment_ws_review_approved"));
        await refreshAll();
      } catch (e: any) {
        const code = e?.response?.data?.error;
        if (code === "WORK_SECURITY_UNSIGNED") {
          toast.error(t("ws_error_unsigned"));
        } else if (code === "WORK_SECURITY_NO_ROLES") {
          toast.error(t("ws_error_no_roles"));
        } else if (code === "WORK_SECURITY_DOCLESS_ROLES") {
          toast.error(t("ws_error_docless_roles"));
        } else {
          toast.error(code || e?.response?.data?.message || t("error"));
        }
        await loadWsStatus();
      } finally {
        setActionLoading(false);
      }
    };
    // Same rule as the backend: only required-at-hiring roles count, so a
    // step holding just optional ones (e.g. cybersecurity) needs the no-roles
    // confirmation too.
    if (wsRequiredRoles.length === 0) {
      askConfirm(t("ws_complete_step_confirm_no_roles"), () => doComplete(true));
    } else {
      doComplete(false);
    }
  };

  // Sphere chip only earns its place once it actually distinguishes
  // something — a single-instruction-area company (today's default) must
  // keep looking exactly like it did before instruction areas existed.
  const wsShowAreaChips =
    new Set((wsStatus?.roles || []).map((item) => item.area?.id || "")).size >
    1;
  // Same idea for the Privaloma dabar / Kita split: an all-required list
  // (today's default everywhere) renders as one flat list, unchanged.
  const wsHasOptionalRoles = (wsStatus?.roles || []).some(
    (item) => item.requiredAtHiring === false
  );
  const wsRequiredRoles = (wsStatus?.roles || []).filter(
    (item) => item.requiredAtHiring !== false
  );
  const wsOptionalRoles = (wsStatus?.roles || []).filter(
    (item) => item.requiredAtHiring === false
  );

  const renderAmendmentWsRoleCard = (
    item: NonNullable<typeof wsStatus>["roles"][number]
  ) => (
    <div key={item.role.id} className="flex flex-col gap-2">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{item.role.name}</p>
          {wsShowAreaChips && item.area && (
            <Badge tone="info">{item.area.name}</Badge>
          )}
        </div>
        {item.role.description && (
          <p className="text-xs text-gray-500">{item.role.description}</p>
        )}
      </div>
      {(item.documents || []).length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          {t("no_document_yet_for_role")}
        </p>
      ) : (
        item.documents.map((doc) => {
          const sigStatus = doc.signature?.status;
          return (
            <div key={doc.id} className="flex items-center gap-3 flex-wrap">
              {sigStatus === "signed" ? (
                <Badge tone="success">{t("signed")}</Badge>
              ) : sigStatus === "rejected" ? (
                <Badge tone="critical">{t("declined")}</Badge>
              ) : (
                <Badge tone="attention">{t("pending")}</Badge>
              )}
              <span className="text-sm">{doc.fileName}</span>
              {sigStatus === "rejected" && doc.signature?.declineReason && (
                <span className="text-xs text-red-600">
                  {t("reason_for_decline_signature")}: {doc.signature.declineReason}
                </span>
              )}
              <button
                type="button"
                className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm max-md:px-3"
                onClick={() => handleViewPdf(doc.fileId)}
              >
                {t("view")}
              </button>
              <button
                type="button"
                className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm max-md:px-3"
                onClick={() =>
                  window.open(
                    `${API_URL}/${companyId}/files/download/${doc.fileId}`,
                    "_blank"
                  )
                }
              >
                {t("download")}
              </button>
              {sigStatus !== "signed" &&
                (wsResentFileIds.has(doc.fileId) ? (
                  <span className="text-xs text-green-600 font-medium">
                    {t("reminder_sent")}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="px-2 py-1 text-xs bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 flex items-center gap-1 max-md:min-h-11 max-md:text-sm max-md:px-3"
                    onClick={() => handleAmendmentWsResend(doc.fileId)}
                    disabled={wsResendingFileId === doc.fileId}
                  >
                    {wsResendingFileId === doc.fileId && (
                      <span className="animate-spin inline-block h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full" />
                    )}
                    {t("resend")}
                  </button>
                ))}
            </div>
          );
        })
      )}
    </div>
  );

  const resetForm = () => {
    setAmendmentNumber("");
    setEffectiveDate("");
    setChanges([EMPTY_CHANGE_ROW]);
    setTermsDraft(EMPTY_TERMS_DRAFT);
    setEditingId(null);
    setShowForm(false);
  };

  const closeAllForms = () => {
    resetForm();
    setShowRegisterForm(false);
    setSettingTermsForId(null);
    setEditingRegisteredId(null);
  };

  const openCreateForm = () => {
    if (readOnly) return;
    closeAllForms();
    setShowForm(true);
  };

  const openRegisterForm = () => {
    if (readOnly) return;
    closeAllForms();
    setShowRegisterForm(true);
  };

  const openSetTermsForm = (amendmentId: string) => {
    closeAllForms();
    setSettingTermsForId(amendmentId);
  };

  const openEditRegisteredForm = (amendmentId: string) => {
    closeAllForms();
    setEditingRegisteredId(amendmentId);
  };

  const openEditForm = (amendment: any) => {
    closeAllForms();
    setAmendmentNumber(amendment.amendmentNumber || "");
    setEffectiveDate(amendment.effectiveDate ? String(amendment.effectiveDate) : "");
    setChanges(changeRowsFromAmendment(amendment));
    setTermsDraft(draftFromAmendment(amendment));
    setEditingId(amendment.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    const termsError = termsErrorKey(termsDraft);
    if (termsError) {
      toast.error(t(termsError));
      return;
    }
    const meaningfulChanges = withoutEmptyChanges(changes);
    if (!hasAnyTerms(termsDraft) && meaningfulChanges.length === 0) {
      toast.error(t("fill_all_fields"));
      return;
    }

    setActionLoading(true);
    try {
      const employmentTerms = buildEmploymentTermsPayload(termsDraft);
      if (editingId) {
        await updateAmendment(companyId, editingId, {
          amendmentNumber,
          effectiveDate: effectiveDate ? Number(effectiveDate) : null,
          changes: meaningfulChanges,
          ...employmentTerms,
        });
        toast.success(t("amendment_updated"));
      } else {
        await createAmendment(companyId, onboardingId, {
          effectiveDate: effectiveDate ? Number(effectiveDate) : null,
          changes: meaningfulChanges,
          ...employmentTerms,
        });
        toast.success(t("amendment_created"));
      }
      resetForm();
      await loadData();
      onChanged?.();
    } catch (err: any) {
      // Known codes (a taken number, missing positions …) get translated
      // text, so no raw code reaches the user; anything else keeps the old message.
      toast.error(
        hasKnownAmendmentCode(err)
          ? describeAmendmentError(err, t)
          : err?.response?.data?.message || err?.response?.data?.error || "Error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    askConfirm(
      t("confirm_delete_amendment"),
      async () => {
        setActionLoading(true);
        try {
          await deleteAmendment(companyId, id);
          toast.success(t("amendment_deleted"));
          await loadData();
          onChanged?.();
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Error");
        } finally {
          setActionLoading(false);
        }
      },
      true
    );
  };

  const handleGenerate = async (id: string) => {
    setActionLoading(true);
    try {
      await generateAmendment(companyId, id);
      toast.success(t("amendment_generated"));
      await loadData();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendForSignature = async (id: string) => {
    setActionLoading(true);
    try {
      const signMethod = signMethodsByAmendment[id] || "dokobit";
      await sendAmendmentForSignature(
        companyId,
        id,
        signMethod === "signontab" ? { signMethod, selectedTabUser } : { signMethod }
      );
      toast.success(t("sent"));
      await loadData();
      onChanged?.();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          (signMethodsByAmendment[id] === "signontab"
            ? t("amendment_signontab_send_failed")
            : "Error")
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = (id: string) => {
    askConfirm(
      t("confirm_revoke_amendment"),
      async () => {
        setActionLoading(true);
        try {
          await revokeAmendment(companyId, id);
          toast.success(t("amendment_revoked"));
          await loadData();
          onChanged?.();
        } catch (err: any) {
          toast.error(err?.response?.data?.error || err?.response?.data?.message || "Error");
        } finally {
          setActionLoading(false);
        }
      },
      true
    );
  };

  const handleUploadSigned = async (id: string, file: File) => {
    setActionLoading(true);
    try {
      const fileId = await uploadAmendmentFile(file, companyId);
      await uploadSignedAmendment(companyId, id, { fileId });
      toast.success(t("amendment_updated"));
      await loadData();
      onReloadOnboarding?.();
    } catch (err: any) {
      // The empty-file case is already toasted by the service (as a
      // translated message) — showing the raw code here too would just add
      // a second, untranslated toast on top of it.
      if (!isEmptyUploadError(err)) {
        toast.error(err?.response?.data?.message || "Error");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewPdf = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  // Signed amendments written as free text only: their terms can still be stated.
  const lacksTerms = (a: any) => a.status === "signed" && a.isEmploymentTermsChange === false;
  const canSetTerms = (a: any) => !readOnly && lacksTerms(a);

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: "bg-gray-100 text-gray-800",
      generated: "bg-blue-100 text-blue-800",
      signing: "bg-yellow-100 text-yellow-800",
      signed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {t(status)}
      </span>
    );
  };

  // Only the first load replaces the panel: a reload must not unmount an open form.
  if (loading && amendments.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">{t("contract_amendments")}</h2>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              onClick={openRegisterForm}
            >
              {t("amendment_register_signed")}
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              onClick={openCreateForm}
            >
              {t("create_amendment")}
            </button>
          </div>
        )}
      </div>

      {leaveLocked && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t("onboarding_locked_during_leave", {
            defaultValue:
              "Onboarding is locked while the employment leave process is open.",
          })}
        </p>
      )}

      {/* Create / Edit form */}
      {showForm && !readOnly && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {editingId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("amendment_number")}
                </label>
                <input
                  type="text"
                  maxLength={AMENDMENT_NUMBER_MAX_LENGTH}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={amendmentNumber}
                  onChange={(e) => setAmendmentNumber(e.target.value)}
                />
              </div>
            )}
            <div>
              <DatePickerx
                title={t("effective_date")}
                selected={effectiveDate && !isNaN(Number(effectiveDate)) ? new Date(Number(effectiveDate)).toISOString().split("T")[0] : ""}
                onChange={(date: Date | null) => setEffectiveDate(date ? String(date.getTime()) : "")}
                isRequired
                disableDatesAfter={null}
              />
            </div>
          </div>

          <AmendmentChangesEditor changes={changes} onChange={setChanges} termsDraft={termsDraft} />
          <EmploymentTermsFields
            companyId={companyId}
            employeeUserId={employeeUserId}
            currentTerms={currentTerms}
            draft={termsDraft}
            onChange={setTermsDraft}
          />

          <div className="flex gap-2">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              onClick={handleSave}
              disabled={actionLoading}
            >
              {actionLoading ? "..." : t("save")}
            </button>
            <button
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              onClick={resetForm}
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      )}

      {showRegisterForm && !readOnly && (
        <RegisterAmendmentForm
          companyId={companyId}
          onboardingId={onboardingId}
          employeeUserId={employeeUserId}
          currentTerms={currentTerms}
          onSaved={async () => {
            setShowRegisterForm(false);
            await reloadAfterTermsChange();
          }}
          onCancel={() => setShowRegisterForm(false)}
        />
      )}

      {/* Amendments list */}
      {amendments.length === 0 && !showForm && !showRegisterForm ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
          <p className="text-sm text-gray-500">{t("no_documents")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {amendments.map((a: any) => {
            const isExpanded = expandedId === a.id;
            const showsTermChanges = a.isEmploymentTermsChange && changesAnyTermGroup(a);
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                {/* Card header — clickable */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setExpandedId(isExpanded ? null : a.id);
                    // The edit form lives inside the row; leaving the row ends the edit.
                    setEditingRegisteredId(null);
                  }}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold">{a.amendmentNumber || "-"}</span>
                    <span className="text-sm text-gray-500">
                      {a.effectiveDate ? new Date(a.effectiveDate).toLocaleDateString("lt-LT") : ""}
                    </span>
                    {statusBadge(a.status)}
                    {a.signMethod === "registered" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {t("amendment_registered_scan")}
                      </span>
                    )}
                    {lacksTerms(a) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500">
                        {t("amendment_terms_not_set")}
                      </span>
                    )}
                    {showsTermChanges && <TermGroupChips amendment={a} />}
                    {a.wsReviewStatus === "pending" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800">
                        {t("amendment_ws_review_pending")}
                      </span>
                    )}
                    {a.employmentTermsAppliedAt && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-800">
                        {t("amendment_terms_applied")}
                      </span>
                    )}
                    {isApprovalAmendment(a) &&
                      approvalsForAmendment(a.id).some((row) => row.status === "pending") && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800">
                        {t("ws_approvals_status_label")}: {t("ws_approvals_waiting")}
                      </span>
                    )}
                    {a.changes !== undefined && (
                      <span className="text-xs text-gray-400">
                        {a.changes?.length || 0} {t("changes").toLowerCase()}
                      </span>
                    )}
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    {showsTermChanges && (
                      <div className="mb-3">
                        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="term-group-chips">
                          <TermGroupChips amendment={a} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {(a.newPositionLT || a.newPositionEN) && (
                            <>
                              {a.newPositionLT && (
                                <div>
                                  <span className="text-gray-500">{t("new_position_lt")}: </span>
                                  <span className="font-medium">{a.newPositionLT}</span>
                                </div>
                              )}
                              {a.newPositionEN && (
                                <div>
                                  <span className="text-gray-500">{t("new_position_en")}: </span>
                                  <span className="font-medium">{a.newPositionEN}</span>
                                </div>
                              )}
                            </>
                          )}
                          {a.newSalary != null && (
                            <div>
                              <span className="text-gray-500">{t("salary")}: </span>
                              <SensitiveAmount className="font-medium" value={a.newSalary} />
                            </div>
                          )}
                          {a.newHoursPerWeek != null && (
                            <div>
                              <span className="text-gray-500">{t("hours_per_week")}: </span>
                              <span className="font-medium">{a.newHoursPerWeek}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {isApprovalAmendment(a) && (
                      <div className="mb-3">
                        <ApprovalStatusLine rows={approvalsForAmendment(a.id)} />
                      </div>
                    )}

                    {/* Legacy «tvarkų peržiūra»: only amendments signed before
                        per-sphere approvals existed still carry it. */}
                    {a.status === "signed" &&
                      a.wsReviewStatus === "pending" &&
                      (a.newPositionLT || a.newPositionEN) && (
                      <div className="mb-3 flex flex-col gap-4">
                        <p className="text-sm text-gray-500">
                          {t("amendment_position_reviews_hint")}
                        </p>

                        {/* === Work Security revision === */}
                        {canManageOnboarding && (
                          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5 shadow-sm">
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-3">
                                  <h2 className="text-lg font-semibold">
                                    {STEP_LABELS.work_security?.[lang] ||
                                      STEP_LABELS.work_security?.en}
                                  </h2>
                                  {a.wsReviewStatus === "approved" ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {t("approved")}
                                    </span>
                                  ) : wsStatus &&
                                    (wsStatus.roles || []).reduce(
                                      (sum, item) =>
                                        sum + (item.documents || []).length,
                                      0
                                    ) > 0 ? (
                                    <Badge
                                      tone={
                                        wsStatus.unsignedCount === 0
                                          ? "success"
                                          : "attention"
                                      }
                                    >
                                      {t("ws_signed_count", {
                                        signed:
                                          (wsStatus.roles || []).reduce(
                                            (sum, item) =>
                                              sum + (item.documents || []).length,
                                            0
                                          ) - (wsStatus.unsignedCount || 0),
                                        total: (wsStatus.roles || []).reduce(
                                          (sum, item) =>
                                            sum + (item.documents || []).length,
                                          0
                                        ),
                                      })}
                                    </Badge>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      {t("pending")}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {a.wsReviewStatus === "pending" &&
                                    wsStatus?.canManageWorkSecurity && (
                                    <button
                                      type="button"
                                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11 max-md:text-sm"
                                      onClick={handleAmendmentManageWsRoles}
                                    >
                                      {t("manage_roles")}
                                    </button>
                                  )}
                                  {a.wsReviewStatus === "pending" && (
                                    <button
                                      type="button"
                                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm"
                                      onClick={() => loadWsStatus()}
                                      disabled={wsLoading}
                                    >
                                      {t("refresh")}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {a.wsReviewStatus === "pending" && (
                                <>
                                  {!wsStatus ? (
                                    wsLoadFailed ? (
                                      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                                        <span className="text-sm">{t("ws_load_failed")}</span>
                                        <button
                                          type="button"
                                          className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11 max-md:text-sm"
                                          onClick={() => loadWsStatus()}
                                        >
                                          {t("refresh")}
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex justify-center p-6">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                                      </div>
                                    )
                                  ) : (
                                    <>
                                      {canManageOnboarding && onSendLoginLink && (
                                        <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                                          <span className="text-sm">
                                            {t("ws_send_login_link_hint")}
                                          </span>
                                          <button
                                            type="button"
                                            className="px-3 py-1.5 bg-white border border-blue-300 text-blue-900 rounded-lg hover:bg-blue-100 text-sm font-medium disabled:opacity-50 whitespace-nowrap max-md:min-h-11 max-md:text-sm"
                                            onClick={() => onSendLoginLink()}
                                            disabled={actionLoading}
                                          >
                                            {t("send_login_link")}
                                          </button>
                                        </div>
                                      )}

                                      {!wsStatus.canManageWorkSecurity && (
                                        <p className="text-sm text-gray-500">
                                          {t("ws_manage_roles_hint")}
                                        </p>
                                      )}

                                      {(wsStatus.roles || []).length === 0 ? (
                                        <p className="text-gray-500">
                                          {t("no_roles_assigned")}
                                        </p>
                                      ) : wsHasOptionalRoles ? (
                                        <div className="flex flex-col gap-4">
                                          <div className="flex flex-col gap-3">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                              {t("ws_required_now")}
                                            </p>
                                            {wsRequiredRoles.map(renderAmendmentWsRoleCard)}
                                          </div>
                                          {wsOptionalRoles.length > 0 && (
                                            <div className="flex flex-col gap-3">
                                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                {t("ws_other_instructions")}
                                              </p>
                                              {wsOptionalRoles.map(renderAmendmentWsRoleCard)}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="flex flex-col gap-4">
                                          {wsStatus.roles.map(renderAmendmentWsRoleCard)}
                                        </div>
                                      )}

                                      {(wsStatus.rolesWithoutActiveDocuments || [])
                                        .length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg flex flex-col gap-2">
                                          <p className="text-sm font-medium">
                                            {t("ws_docless_roles_warning")}{" "}
                                            {wsStatus.rolesWithoutActiveDocuments
                                              .map((r) => r.name)
                                              .join(", ")}
                                          </p>
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                              checked={wsAckDocless}
                                              onChange={(e) =>
                                                setWsAckDocless(e.target.checked)
                                              }
                                            />
                                            <span className="text-sm">
                                              {t("ws_docless_roles_ack")}
                                            </span>
                                          </label>
                                        </div>
                                      )}

                                      <div className="pt-2">
                                        <button
                                          type="button"
                                          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                          disabled={
                                            actionLoading ||
                                            wsLoading ||
                                            // Only roles required at hiring gate the
                                            // revision (see complete-ws-revision.js).
                                            (wsStatus.requiredUnsignedCount || 0) > 0 ||
                                            ((wsStatus.rolesWithoutActiveDocuments || [])
                                              .length > 0 &&
                                              !wsAckDocless)
                                          }
                                          onClick={() =>
                                            handleCompleteWsRevision(a.id)
                                          }
                                        >
                                          {actionLoading && (
                                            <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                          )}
                                          {t("complete_step")}
                                        </button>
                                        {(wsStatus.requiredUnsignedCount || 0) > 0 && (
                                          <p className="text-sm text-red-500 mt-1">
                                            {t("ws_error_unsigned")}
                                          </p>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Changes list */}
                    {a.changes?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t("changes")}</p>
                        <div className="flex flex-col gap-1.5">
                          {a.changes.map((c: any, ci: number) => (
                            <div key={ci} className="flex gap-2 text-sm">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${c.action === "new" ? "bg-green-100 text-green-700" :
                                c.action === "remove" ? "bg-red-100 text-red-700" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                {c.action === "new" ? t("new_point") : c.action === "remove" ? t("remove_point") : t("change_existing")}
                              </span>
                              <span className="font-medium text-gray-700">p. {c.point}</span>
                              {c.text && <span className="text-gray-500">{c.text}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Signatures (amendment document signing) */}
                    {(a.status === "signing" || a.status === "signed") &&
                      a.signatures?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t("signatures")}</p>
                        <div className="flex flex-wrap gap-3">
                          {a.signatures.map((sig: any) => (
                            <div key={sig.id} className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sig.status === "signed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {sig.status === "signed" ? t("signed") : t("pending")}
                              </span>
                              <span className="text-sm text-gray-600">{getSignatureUserLabel(sig)}</span>
                              {sig.signingTime && (
                                <span className="text-xs text-gray-400">{new Date(sig.signingTime).toLocaleString("lt-LT")}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {settingTermsForId === a.id && canSetTerms(a) && (
                      <SetAmendmentTermsForm
                        companyId={companyId}
                        amendmentId={a.id}
                        employeeUserId={employeeUserId}
                        currentTerms={currentTerms}
                        onFinished={async () => {
                          setSettingTermsForId(null);
                          await reloadAfterTermsChange();
                        }}
                        onCancel={() => setSettingTermsForId(null)}
                      />
                    )}

                    {editingRegisteredId === a.id && !readOnly && (
                      <div className="mb-3">
                        <RegisterAmendmentForm
                          companyId={companyId}
                          onboardingId={onboardingId}
                          employeeUserId={employeeUserId}
                          currentTerms={currentTerms}
                          amendment={a}
                          onSaved={async () => {
                            setEditingRegisteredId(null);
                            await reloadAfterTermsChange();
                          }}
                          onCancel={() => setEditingRegisteredId(null)}
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                      {!readOnly && a.status === "draft" && (
                        <>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => openEditForm(a)}>{t("edit")}</button>
                          <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleGenerate(a.id)} disabled={actionLoading}>{t("generate")}</button>
                          <button className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleDelete(a.id)} disabled={actionLoading}>{t("delete")}</button>
                        </>
                      )}
                      {!readOnly && a.status === "generated" && (
                        <>
                          {a.fileId && (
                            <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => handleViewPdf(a.fileId)}>{t("view")}</button>
                          )}
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => openEditForm(a)}>{t("edit")}</button>
                          <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleGenerate(a.id)} disabled={actionLoading}>{t("regenerate")}</button>
                          <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-base md:text-xs max-md:min-h-11" value={signMethodsByAmendment[a.id] || "dokobit"} onChange={(e) => setSignMethodsByAmendment((prev) => ({ ...prev, [a.id]: e.target.value as AmendmentSignMethod }))}>
                            <option value="dokobit">Dokobit</option>
                            <option value="signontab">SignOnTab</option>
                            <option value="manual">{t("manual_signature")}</option>
                          </select>
                          {signMethodsByAmendment[a.id] === "signontab" && (
                            tabUsersFailed ? (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600">
                                {t("amendment_signontab_tablets_failed")}
                                <button type="button" className="underline hover:text-red-700" onClick={() => setTabUsersFailedFor(null)}>{t("retry")}</button>
                              </span>
                            ) : companyTabUsers === null ? (
                              <span className="text-xs text-gray-500">{t("loading")}</span>
                            ) : companyTabUsers.length === 0 ? (
                              <span className="text-xs text-red-600">{t("amendment_signontab_no_tablets")}</span>
                            ) : (
                              <select aria-label={t("amendment_signontab_tablet")} title={t("amendment_signontab_tablet")} className="border border-gray-300 rounded-lg px-2 py-1.5 text-base md:text-xs max-md:min-h-11" value={selectedTabUser} onChange={(e) => setSelectedTabUser(e.target.value)}>
                                {companyTabUsers.map((u) => (
                                  <option key={u.tabId} value={u.tabId}>{u.name}</option>
                                ))}
                              </select>
                            )
                          )}
                          <button className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleSendForSignature(a.id)} disabled={actionLoading || (signMethodsByAmendment[a.id] === "signontab" && !tabUserReady)}>{t("send_for_signature")}</button>
                          <button className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleDelete(a.id)} disabled={actionLoading}>{t("delete")}</button>
                        </>
                      )}
                      {!readOnly && a.status === "signing" && (
                        <>
                          {a.fileId && (
                            <>
                              <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => handleViewPdf(a.fileId)}>{t("view")}</button>
                              <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => window.open(`${API_URL}/${companyId}/files/download/${a.fileId}`, "_blank")}>{t("download")}</button>
                            </>
                          )}
                          {a.signMethod === "signontab" && (
                            <span className="text-xs text-gray-500">{t("amendment_signontab_waiting")}</span>
                          )}
                          {a.signMethod === "manual" && (
                            <label className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium cursor-pointer max-md:min-h-11 max-md:text-sm max-md:inline-flex max-md:items-center">
                              {t("upload")}
                              <input type="file" className="hidden" accept=".pdf,.asice" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadSigned(a.id, file); }} />
                            </label>
                          )}
                          <button className="px-3 py-1.5 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-50 text-xs font-medium disabled:opacity-50 max-md:min-h-11 max-md:text-sm" onClick={() => handleRevoke(a.id)} disabled={actionLoading}>{t("revoke")}</button>
                        </>
                      )}
                      {(a.status === "signed" || (readOnly && a.fileId)) &&
                        a.fileId && (
                        <>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => handleViewPdf(a.fileId)}>{t("view")}</button>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => window.open(`${API_URL}/${companyId}/files/download/${a.fileId}`, "_blank")}>{t("download")}</button>
                        </>
                      )}
                      {canSetTerms(a) && (
                        <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => openSetTermsForm(a.id)}>{t("amendment_set_terms")}</button>
                      )}
                      {!readOnly && a.signMethod === "registered" && (
                        <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:text-sm" onClick={() => openEditRegisteredForm(a.id)}>{t("edit")}</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={!!confirmState}
        message={confirmState?.message || ""}
        destructive={confirmState?.destructive}
        onConfirm={handleConfirmYes}
        onClose={closeConfirm}
      />
    </div>
  );
}
